import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import {
  Card,
  CardMedia,
  CardContent,
  Icon,
  IconButton,
  Checkbox,
  Tooltip,
  Typography
} from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import clsx from "clsx";
import React from "react";
import {
  canViewFile,
  checkNullOrUndefined,
  getFileImageSrc,
  handleDownload,
  onFileViwerClick,
} from "./AuraFunctions";

const ImageRenderer = ({
  onChange = () => {},
  setSelected = () => {},
  attachments = [],
  onClear,
  selected,
  value,
  showCheckbox,
  handleSelectionMedia,
}) => {
  return (
    <div className="flex">
      {attachments?.map((media) => {
        return (
          <div>
            {showCheckbox && (
              <div>
                <Checkbox
                  className="flex-start"
                  //   checked={selectedImage?.[sInd]?.[index]?.[selectedImageIndex] || false}
                  onChange={(event) => handleSelectionMedia(media)}
                />
              </div>
            )}
            <div
              onClick={() => {
                // reset()
                onChange(media.id);
                setSelected(media);
                onFileViwerClick(media);
              }}
              onKeyDown={() => onChange(media.id)}
              role="button"
              tabIndex={0}
              className={clsx(
                "attachmentItem flex items-center justify-center relative w-128 h-128 rounded-16 mx-12 overflow-hidden cursor-pointer outline-none shadow hover:shadow-lg",
                media.id === value && "featured",
                selected === media.id && "border-blue-500"
              )}
              key={media.id}
            >
              {onClear && (
                <div className="absolute top-0 right-0 bg-grey">
                  <IconButton
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onClear(ev, media);
                    }}
                    size="small"
                  >
                    <Icon>clear</Icon>
                  </IconButton>
                </div>
              )}
              <img
                src={getFileImageSrc(media)}
                className="max-w-none w-auto h-full"
                alt="attachment"
              />
              {selected === media.id && (
                <div className="selection absolute flex items-center justify-center w-14 h-14 bg-white rounded-full">
                  <FuseSvgIcon size={28} color="success">
                    heroicons-solid:check-circle
                  </FuseSvgIcon>
                </div>
              )}
            </div>
            <p className="mb-24 text-center w-96 m-auto text-sm">
              {media?.filename || media?.name}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export const SimpleCardDocumentsViewer = ({
  attachments = [],
  width = "w-[16rem]",
  height = "h-[13rem]",
  flexDirection = "sm:flex-row",
  isPrinting = false,
  fontSize = "text-14",
  hideFileName = false,
  showSupersededBadge = false,
}) => {
  return (
    <div className={`flex flex-wrap gap-20 ${!isPrinting ? `flex-col ${flexDirection}` : ""}`}>
      {attachments?.map((media) => {
        const superseded =
          showSupersededBadge &&
          (media?.is_superseded === true || media?.isSuperseded === true);
        return (
          <div key={media.id || media.name} className="relative">
            {superseded && (
              <Typography
                className="absolute top-6 left-6 z-10 rounded bg-gray-700 px-6 py-2 text-10 font-semibold text-white"
              >
                Superseded
              </Typography>
            )}
            <Card className={`${width} rounded-md `}>
              <CardMedia
                className={`${width} ${height} cursor-pointer bg-contain`}
                image={getFileImageSrc(media)}
                title="Click here to view or download document"
                onClick={() => {
                  onFileViwerClick(media);
                }}
              />
              <CardContent className="!p-0">
                {!hideFileName && (
                  <Tooltip title={media?.filename || media?.name}>
                    <Typography className={`truncate text-center px-10 py-4 ${fontSize}`}>
                      {media?.filename || media?.name}
                    </Typography>
                  </Tooltip>
                )}
                {!isPrinting && (
                  <div className="flex flex-row items-center justify-center border-t space-x-12 px-10 py-6 w-full bg-[#f3f3f3]">
                    {canViewFile(media) && (
                      <Typography
                        color="secondary"
                        className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                        onClick={() => onFileViwerClick(media)}
                      >
                        View
                      </Typography>
                    )}
                    <Typography
                      color="secondary"
                      className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                      onClick={() => handleDownload(media)}
                    >
                      Download
                    </Typography>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Card viewer with selectable check box
 */
export const SimpleSelectableCardDocumentsViewer = ({
  attachments = [],
  width = "w-[16rem]",
  height = "h-[13rem]",
  flexDirection = "sm:flex-row",
  fontSize = "text-14",
  selected = [], // Selected attachment list
  handleSelectionMedia = () => {}, // Handle select/unselect attachments
}) => {
  return (
    <div className={`flex flex-wrap gap-20 flex-col ${flexDirection}`}>
      {attachments?.map((media) => {
        const isSelected = selected.some((item) => item.id === media.id);

        return (
          <div key={media.id || media.name} className="relative">
            <Card
              className={clsx(
                `${width} rounded-md overflow-hidden relative group`,
                isSelected && "border-1 border-green"
              )}
            >
              <div className="absolute top-4 left-4 z-10 bg-white rounded-none">
                <Checkbox
                  className="p-0"
                  checked={isSelected}
                  onChange={() => handleSelectionMedia(media)}
                  size="small"
                  inputProps={{
                    "aria-label": `Select ${media?.filename || media?.name}`,
                  }}
                />
              </div>

              <div className="cursor-pointer">
                <CardMedia
                  className={`${width} ${height} bg-contain`}
                  image={getFileImageSrc(media)}
                  title="Click here to view or download document"
                  onClick={() => {
                    handleSelectionMedia(media);
                  }}
                />
              </div>

              <CardContent className="!p-0">
                <Tooltip title={media?.filename || media?.name}>
                  <Typography className={`truncate text-center px-10 py-4 ${fontSize}`}>
                    {media?.filename || media?.name}
                  </Typography>
                </Tooltip>

                <div className="flex flex-row items-center justify-center border-t space-x-12 px-10 py-6 w-full bg-[#f3f3f3]">
                  {canViewFile(media) && (
                    <Typography
                      color="secondary"
                      className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                      onClick={() => onFileViwerClick(media)}
                    >
                      View
                    </Typography>
                  )}
                  <Typography
                    color="secondary"
                    className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                    onClick={() => handleDownload(media)}
                  >
                    Download
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

export const HistoryCardDocumentsViewer = ({
  attachments = [],
  width = "w-[16rem]",
  height = "h-[13rem]",
  flexDirection = "sm:flex-row",
  isPrinting = false,
  fontSize = "text-14"
}) => {
  return (
    <div className={`flex flex-wrap gap-20 ${!isPrinting ? `flex-col ${flexDirection}` : ""}`}>
      {attachments?.map((attachment) => {
        const media = attachment?.files;
        if(!media) return <></>;
        return (
          <div key={media.id || media.name}>
            <Card className={`${width} rounded-md relative`}>
              {(attachment?.change_type === "ADDED" || attachment?.change_type === "DELETED") && (
                <div className="absolute top-2 right-4 z-10">
                  {attachment.change_type === "ADDED" ? (
                    <AddCircleIcon fontSize="small" className="bg-white p-0 rounded-xl"  color="success" />
                  ) : (
                    <CancelIcon fontSize="small" color="error" />
                  )}
                </div>
              )}
              <CardMedia
                className={`${width} ${height} cursor-pointer bg-contain`}
                image={getFileImageSrc(media)}
                title="Click here to view or download document"
                onClick={() => {
                  onFileViwerClick(media);
                }}
              />
              <CardContent className="!p-0">
                <Tooltip title={media?.filename || media?.name}>
                  <Typography className={`truncate text-center px-10 py-4 ${fontSize}`}>
                    {media?.filename || media?.name}
                  </Typography>
                </Tooltip>
                {!isPrinting && (
                  <div className="flex flex-row items-center justify-center border-t space-x-12 px-10 py-6 w-full bg-[#f3f3f3]">
                    {canViewFile(media) && (
                      <Typography
                        color="secondary"
                        className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                        onClick={() => onFileViwerClick(media)}
                      >
                        View
                      </Typography>
                    )}
                    <Typography
                      color="secondary"
                      className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                      onClick={() => handleDownload(media)}
                    >
                      Download
                    </Typography>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

export default ImageRenderer;
